import { CancelToken } from 'axios'
import { capitalize } from 'lodash'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'

import { Item } from '../../models/Item'
import { generateCancelToken } from '../../modules/apiclient'
import { computeHash } from '../../utils'
import {
    AvailabilityByHash,
    AvailabilityFilter,
    AvailabiltySearchParams,
    getAvailabilityByDate,
    getCalendarMonthByDate,
    getDateLabelForView,
} from '../../utils/calendar'
import { DAYS_OF_WEEK } from '../../utils/dates'
import { Dots } from '../Dots'
import { Icon } from '../Icon'
import Button from '../app/design/Button'
import Label from '../app/design/Label'
import { CalendarAvailabilityDateButton } from './CalendarAvailabilityDateButton'

type Props = {
    searchParams: AvailabiltySearchParams
    selectedDay: moment.Moment | null
    selectedMonth: moment.Moment
    selectedItem: Item | null | undefined
    availabilityByHash: AvailabilityByHash
    availabilityFilter: AvailabilityFilter
    onGoToToday: () => void
    onSkipMonth: (direction: 'left' | 'right') => void
    onSelectDate: (date: moment.Moment) => void
}

export const CalendarAvailabilityMiniCalendar = ({
    searchParams,
    selectedDay,
    selectedMonth,
    selectedItem,
    availabilityByHash,
    availabilityFilter,
    onGoToToday,
    onSkipMonth,
    onSelectDate,
}: Props) => {
    const weeksToRender = useMemo(() => getCalendarMonthByDate(selectedMonth), [selectedMonth])

    const [isSearching, setIsSearching] = useState(false)
    const [availabilityByDate, setAvailabilityByDate] = useState({})

    const searchCalendarAvailability = (
        params: AvailabiltySearchParams,
        hash: string,
        cancelToken: CancelToken
    ) => {
        console.log('[todo] searchCalendarAvailability', params, hash, cancelToken)
    }

    useEffect(() => {
        setIsSearching(true)

        const hash = Math.abs(computeHash(JSON.stringify(searchParams))).toString()
        if (hash in availabilityByHash) {
            setAvailabilityByDate(getAvailabilityByDate(availabilityByHash[hash] ?? []))
            setIsSearching(false)
            return
        }

        const cancelToken = generateCancelToken()
        searchCalendarAvailability(searchParams, hash, cancelToken.token)

        return () => {
            if (cancelToken) {
                cancelToken.cancel('cancel search availability for selected month')
            }
        }
    }, [searchParams, availabilityByHash])

    return (
        <div className="flex flex-col rounded-lg bg-white">
            <div className="flex items-center gap-2 pt-[20px] pr-[18px] pb-[21px] pl-[24px]">
                <Label size="lg" className="ml-[6px] text-gray-700">
                    {getDateLabelForView(selectedMonth, 'availability')}
                </Label>

                {isSearching && <Dots />}

                <div className="ml-auto flex gap-1">
                    <Button
                        shape="square"
                        size="small"
                        intent="tertiary_deprecated"
                        onClick={() => onSkipMonth('left')}
                    >
                        <Icon icon="chevronLeft.medium" />
                    </Button>

                    <Button
                        shape="square"
                        size="small"
                        intent="tertiary_deprecated"
                        onClick={() => onSkipMonth('right')}
                    >
                        <Icon icon="chevronRight.medium" />
                    </Button>

                    <Button
                        shape="square"
                        size="small"
                        intent="tertiary_deprecated"
                        style={{ marginRight: 4 }}
                        onClick={onGoToToday}
                    >
                        <Icon icon="calendar2.medium" />
                    </Button>
                </div>
            </div>

            <div className="flex flex-col gap-[2px] p-6">
                <div className="flex justify-between">
                    {DAYS_OF_WEEK.map((d) => (
                        <Label
                            key={d}
                            size="xs"
                            className="w-[45px] px-2 py-[3px] text-center text-gray-600"
                        >
                            {capitalize(d).slice(0, 3)}
                        </Label>
                    ))}
                </div>

                {weeksToRender.map((week) => (
                    <div key={week[0].toISOString()} className="flex justify-between">
                        {week.map((day) => (
                            <CalendarAvailabilityDateButton
                                key={day.toISOString()}
                                day={day}
                                selectedDay={selectedDay}
                                selectedMonth={selectedMonth}
                                selectedItem={selectedItem}
                                availabiltyByDate={availabilityByDate}
                                availabilityFilter={availabilityFilter}
                                onSelect={(date) => onSelectDate(date)}
                            />
                        ))}
                    </div>
                ))}
            </div>
        </div>
    )
}
